import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type ListTableColumnWidth = 'auto' | 'fill'
export type ListTableSortDirection = 'asc' | 'desc'

export type ListTableSort = {
  columnId: string
  direction: ListTableSortDirection
}

export type ListTableColumn<T> = {
  id: string
  header: string
  cell: (row: T) => ReactNode
  width?: ListTableColumnWidth
  headerClassName?: string
  cellClassName?: string
}

const FILL_WIDTH_CLASS_NAME = {
  1: 'w-full',
  2: 'w-1/2',
  3: 'w-1/3',
  4: 'w-1/4',
  5: 'w-1/5',
  6: 'w-1/6',
} as const

const columnWidthClassName = (
  width: ListTableColumnWidth | undefined,
  fillCount: number
) => {
  if (width === 'auto') {
    return 'w-0 whitespace-nowrap'
  }

  if (width === 'fill') {
    return (
      FILL_WIDTH_CLASS_NAME[fillCount as keyof typeof FILL_WIDTH_CLASS_NAME] ??
      'w-full'
    )
  }

  return undefined
}

const activateCellLink = (event: MouseEvent<HTMLTableCellElement>) => {
  const link = event.currentTarget.querySelector<HTMLAnchorElement>('a[href]')
  if (!link) {
    return
  }

  event.stopPropagation()

  if (link.contains(event.target as Node)) {
    return
  }

  link.click()
}

export type ListTableProps<T> = {
  columns: ListTableColumn<T>[]
  data: T[]
  getRowKey: (row: T) => string | number
  emptyMessage?: string
  className?: string
  onRowClick?: (row: T) => void
  sort?: ListTableSort
  onSort?: (columnId: string) => void
  getRowClassName?: (row: T) => string | undefined
}

const sortIcon = (columnId: string, sort?: ListTableSort) => {
  if (sort?.columnId !== columnId) {
    return ArrowUpDown
  }

  return sort.direction === 'asc' ? ArrowUp : ArrowDown
}

const ariaSort = (columnId: string, sort?: ListTableSort) => {
  if (sort?.columnId !== columnId) {
    return 'none'
  }

  return sort.direction === 'asc' ? 'ascending' : 'descending'
}

export const ListTable = <T,>({
  columns,
  data,
  getRowKey,
  emptyMessage = 'No results found.',
  className,
  onRowClick,
  sort,
  onSort,
  getRowClassName,
}: ListTableProps<T>) => {
  const fillCount = columns.filter((column) => column.width === 'fill').length

  return (
    <div className={cn('overflow-x-auto rounded-xl', className)}>
      <table className="w-full min-w-full border-collapse text-left">
        <thead>
          <tr className="bg-primary-dark/80 text-white">
            {columns.map((column) => {
              const SortIcon = sortIcon(column.id, sort)

              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={onSort ? ariaSort(column.id, sort) : undefined}
                  className={cn(
                    'font-semibold border border-outline/40',
                    onSort ? 'p-0' : 'px-4 py-6',
                    columnWidthClassName(column.width, fillCount),
                    column.headerClassName
                  )}
                >
                  {onSort ? (
                    <button
                      type="button"
                      className="inline-flex w-full cursor-pointer items-center gap-2 px-4 py-6 text-left font-semibold"
                      onClick={() => onSort(column.id)}
                    >
                      <span className="whitespace-pre-line">{column.header}</span>
                      <SortIcon
                        className={cn(
                          'size-4 shrink-0',
                          sort?.columnId === column.id ? 'opacity-100' : 'opacity-60'
                        )}
                        aria-hidden
                      />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody className="text-sm">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-muted-foreground"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => {
              const rowClassName = getRowClassName?.(row)

              return (
                <tr
                  key={getRowKey(row)}
                  className={cn(
                    index % 2 === 0 ? 'bg-white' : 'bg-surface-muted/60',
                    onRowClick && 'cursor-pointer hover:bg-surface-muted/80',
                    rowClassName
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onRowClick(row)
                        }
                      }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={cn(
                        'relative px-4 py-6 text-foreground border border-outline/40',
                        columnWidthClassName(column.width, fillCount),
                        column.cellClassName,
                        rowClassName
                      )}
                      onClick={activateCellLink}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
